/**
 * ORDENES PAGE - Gestión de Órdenes con tabs
 * Tabs: Órdenes | Cotizaciones
 */

import { useState, useCallback, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import type { ColumnDef } from "@tanstack/react-table";
import {
  ClipboardList,
  Plus,
  MoreHorizontal,
  CheckCircle,
  CheckCircle2,
  XCircle,
  AlertCircle,
  RefreshCw,
  Trash2,
  Search,
  X,
  Eye,
  Ban,
  Clock,
  FlaskConical,
  TestTubes,
  FileText,
  CreditCard,
  Pencil,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  DataTableColumnHeader,
  DataTablePagination,
  DataTableViewOptions,
} from "@/components/ui/data-table";
import { ResponsiveTable, useIsMobile } from "@/components/ui/responsive-table";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { DateRangeFilter } from "@/components/ui/date-range-filter";

import { useApiError } from "@/hooks/use-api-error";
import { useServerDataTable } from "@/hooks/use-server-data-table";
import { useSucursal, ALL_SUCURSALES_ID } from "@/contexts/sucursal-context";
import { useSucursales } from "@/hooks/use-sucursales";
import {
  useDeactivateOrden,
  useActivateOrden,
  useDeleteOrden,
  useDarDeBajaOrden,
  useCambiarEstadoOrden,
  useGenerarReciboPdf,
  useAnularPagoOrden,
  getOrdenes,
  type OrdenListItem,
  type PagoOrdenResult,
  type EstadoOrden,
  type TipoServicio,
} from "@/hooks/use-ordenes";

import { useAuth } from "@/contexts/auth-context";
import { OrdenFormDialog } from "./components/orden-form-dialog";
import { OrdenDetailSheet } from "./components/orden-detail-sheet";
import { OrdenEditFormDialog } from "./components/orden-edit-form-dialog";
import { PagoOrdenFormDialog } from "./components/pago-orden-form-dialog";
import { CotizacionesCatalogo } from "./components/cotizaciones-catalogo";
import { OrdenExamenesTab } from "./components/orden-examenes-tab";
import {
  getValidTransitions,
  getTransitionMeta,
  canDarDeBaja as canDarDeBajaFn,
} from "./utils/orden-transitions";
import { canPagar, canAnularPago } from "./utils/orden-payment";
import { shouldShowEditButton } from "./utils/orden-edit-utils";
import { EstadoOrdenBadge } from "./components/estado-orden-badge";
import { TipoCobroBadge } from "./components/tipo-cobro-badge";
import { TipoServicioBadge } from "@/components/tipo-servicio-badge";

function getPrioridadBadgeVariant(
  prioridad: string
): "default" | "secondary" | "destructive" | "outline" {
  switch (prioridad) {
    case "URGENTE":
      return "destructive";
    case "STAT":
      return "destructive";
    default:
      return "outline";
  }
}

export default function OrdenesPage() {
  const { t } = useTranslation();
  const { getErrorMessage } = useApiError();
  const { hasPermission } = useAuth();
  const isMobile = useIsMobile();
  const location = useLocation();
  const { isAllSucursalesSelected, sucursalActiva, canViewAllSucursales } = useSucursal();
  const { data: sucursales = [] } = useSucursales();

  // Dialog state
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isCancelOpen, setIsCancelOpen] = useState(false);
  const [selectedOrdenId, setSelectedOrdenId] = useState<number | null>(null);
  const [selectedOrden, setSelectedOrden] = useState<OrdenListItem | null>(null);
  const [confirmAction, setConfirmAction] = useState<"activate" | "deactivate" | "delete" | null>(
    null
  );
  const [cancelMotivo, setCancelMotivo] = useState("");
  const [activeTab, setActiveTab] = useState("ordenes");
  const [transitionTarget, setTransitionTarget] = useState<{
    orden: OrdenListItem;
    estado: EstadoOrden;
  } | null>(null);
  const [initialPacienteId, setInitialPacienteId] = useState<number | null>(null);
  const [initialPacienteNombre, setInitialPacienteNombre] = useState("");
  const [initialPacienteDesc, setInitialPacienteDesc] = useState("");
  const [initialPacienteEdadYears, setInitialPacienteEdadYears] = useState<number | undefined>(
    undefined
  );
  const [isPagoOpen, setIsPagoOpen] = useState(false);
  const [isAnularPagoOpen, setIsAnularPagoOpen] = useState(false);
  const [pagoOrden, setPagoOrden] = useState<OrdenListItem | null>(null);
  const [editOrdenId, setEditOrdenId] = useState<number | null>(null);
  const [isEditOpen, setIsEditOpen] = useState(false);

  // Abrir form con paciente preseleccionado si viene de la página de pacientes
  // o abrir form vacío si viene del dashboard con openNewOrder
  useEffect(() => {
    const state = location.state as {
      pacienteId?: number;
      pacienteNombre?: string;
      pacienteDesc?: string;
      pacienteEdadYears?: number;
      openNewOrder?: boolean;
      openOrdenId?: number;
    } | null;
    if (state?.pacienteId) {
      setInitialPacienteId(state.pacienteId);
      setInitialPacienteNombre(state.pacienteNombre ?? "");
      setInitialPacienteDesc(state.pacienteDesc ?? "");
      setInitialPacienteEdadYears(state.pacienteEdadYears);
      setIsFormOpen(true);
      window.history.replaceState({}, "");
    } else if (state?.openNewOrder) {
      setIsFormOpen(true);
      window.history.replaceState({}, "");
    } else if (state?.openOrdenId) {
      setSelectedOrdenId(state.openOrdenId);
      setIsDetailOpen(true);
      window.history.replaceState({}, "");
    }
  }, [location.state]);

  // Determinar filtro inicial de sucursal basado en contexto
  const getInitialSucursalFilter = useCallback(() => {
    if (canViewAllSucursales && isAllSucursalesSelected) {
      return "all";
    }
    if (sucursalActiva && sucursalActiva.id !== ALL_SUCURSALES_ID) {
      return String(sucursalActiva.id);
    }
    return "all";
  }, [canViewAllSucursales, isAllSucursalesSelected, sucursalActiva]);

  // Mutations
  const deactivateMutation = useDeactivateOrden({
    onSuccess: () => {
      toast.warning(t("ordenes.toast.deactivated", { numero: selectedOrden?.numero }));
      refetch();
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  const activateMutation = useActivateOrden({
    onSuccess: () => {
      toast.success(t("ordenes.toast.activated", { numero: selectedOrden?.numero }));
      refetch();
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  const deleteMutation = useDeleteOrden({
    onSuccess: () => {
      toast.error(t("ordenes.toast.deleted", { numero: selectedOrden?.numero }));
      refetch();
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  const darDeBajaMutation = useDarDeBajaOrden({
    onSuccess: () => {
      toast.warning(t("ordenes.toast.cancelled"));
      refetch();
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  const cambiarEstadoMutation = useCambiarEstadoOrden({
    onSuccess: () => {
      if (transitionTarget) {
        toast.success(
          t("ordenes.transitions.success", {
            numero: transitionTarget.orden.numero,
            estado: transitionTarget.estado,
          })
        );
      }
      setTransitionTarget(null);
      refetch();
    },
    onError: (err) => {
      toast.error(getErrorMessage(err));
      setTransitionTarget(null);
    },
  });

  const anularPagoMutation = useAnularPagoOrden({
    onSuccess: () => {
      toast.success(t("ordenes.pago.successCancel"));
      setIsAnularPagoOpen(false);
      setPagoOrden(null);
      refetch();
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  // Handlers
  const handleViewDetail = useCallback((orden: OrdenListItem) => {
    setSelectedOrdenId(orden.id);
    setIsDetailOpen(true);
  }, []);

  const handleToggleStatus = useCallback((orden: OrdenListItem) => {
    setSelectedOrden(orden);
    setConfirmAction(orden.is_active ? "deactivate" : "activate");
    setIsConfirmOpen(true);
  }, []);

  const handleDelete = useCallback((orden: OrdenListItem) => {
    setSelectedOrden(orden);
    setConfirmAction("delete");
    setIsConfirmOpen(true);
  }, []);

  const handleCancelOrder = useCallback((orden: OrdenListItem) => {
    setSelectedOrden(orden);
    setCancelMotivo("");
    setIsCancelOpen(true);
  }, []);

  const handlePagar = useCallback((orden: OrdenListItem) => {
    setPagoOrden(orden);
    setIsPagoOpen(true);
  }, []);

  const handleAnularPago = useCallback((orden: OrdenListItem) => {
    setPagoOrden(orden);
    setIsAnularPagoOpen(true);
  }, []);

  const handleEdit = useCallback((orden: OrdenListItem) => {
    setEditOrdenId(orden.id);
    setIsEditOpen(true);
  }, []);

  const handleTransition = useCallback((orden: OrdenListItem, estado: EstadoOrden) => {
    setTransitionTarget({ orden, estado });
  }, []);

  const handleConfirmTransition = () => {
    if (!transitionTarget) return;
    cambiarEstadoMutation.mutate({
      id: transitionTarget.orden.id,
      estado: transitionTarget.estado,
    });
  };

  const handleConfirmAction = () => {
    if (!selectedOrden || !confirmAction) return;
    if (confirmAction === "activate") activateMutation.mutate(selectedOrden.id);
    else if (confirmAction === "deactivate") deactivateMutation.mutate(selectedOrden.id);
    else if (confirmAction === "delete") deleteMutation.mutate(selectedOrden.id);
    setIsConfirmOpen(false);
    setSelectedOrden(null);
    setConfirmAction(null);
  };

  const handleConfirmCancel = () => {
    if (!selectedOrden) return;
    darDeBajaMutation.mutate({ id: selectedOrden.id, motivo: cancelMotivo });
    setIsCancelOpen(false);
    setSelectedOrden(null);
    setCancelMotivo("");
  };

  const handleFormSuccess = () => {
    setIsFormOpen(false);
    toast.success(t("ordenes.toast.created"));
    refetch();
  };

  const reciboPdfMutation = useGenerarReciboPdf();

  const handleDownloadRecibo = useCallback(
    (orden: OrdenListItem) => reciboPdfMutation.mutate(orden.id),
    [reciboPdfMutation]
  );

  // Columns
  const columns: ColumnDef<OrdenListItem>[] = useMemo(
    () => [
      {
        accessorKey: "numero",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t("ordenes.columns.number")} />
        ),
        cell: ({ row }) => (
          <button
            className="font-mono text-sm font-medium text-primary hover:underline"
            onClick={() => handleViewDetail(row.original)}
          >
            {row.original.numero}
          </button>
        ),
      },
      {
        accessorKey: "paciente_nombre",
        header: t("ordenes.columns.patient"),
        cell: ({ row }) => <span className="font-medium">{row.original.paciente_nombre}</span>,
        enableSorting: false,
      },
      {
        accessorKey: "cliente_nombre",
        header: t("ordenes.columns.client"),
        cell: ({ row }) => (
          <span className="text-sm text-muted-foreground">
            {row.original.cliente_nombre || t("ordenes.clientParticular")}
          </span>
        ),
        enableSorting: false,
      },
      ...(isAllSucursalesSelected
        ? [
            {
              accessorKey: "sucursal_nombre" as const,
              header: () => t("ordenes.columns.branch"),
              cell: ({ row }: { row: { original: OrdenListItem } }) => (
                <span className="text-sm text-muted-foreground">
                  {row.original.sucursal_nombre}
                </span>
              ),
              enableSorting: false,
            },
          ]
        : []),
      {
        accessorKey: "fecha",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t("ordenes.columns.date")} />
        ),
        cell: ({ row }) => (
          <span className="text-sm text-muted-foreground">
            {new Date(row.original.fecha).toLocaleDateString()}
          </span>
        ),
      },
      {
        accessorKey: "prioridad",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t("ordenes.columns.priority")} />
        ),
        cell: ({ row }) => (
          <Badge variant={getPrioridadBadgeVariant(row.original.prioridad)}>
            {row.original.prioridad_display}
          </Badge>
        ),
      },
      {
        accessorKey: "estado",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t("ordenes.columns.status")} />
        ),
        cell: ({ row }) => (
          <EstadoOrdenBadge estado={row.original.estado} label={row.original.estado_display} />
        ),
      },
      {
        accessorKey: "tipo_servicio",
        header: t("ordenes.fields.serviceType"),
        cell: ({ row }) => <TipoServicioBadge tipo={row.original.tipo_servicio as TipoServicio} />,
        enableSorting: false,
      },
      {
        accessorKey: "total_examenes",
        header: t("ordenes.columns.exams"),
        cell: ({ row }) => (
          <span className="text-sm text-muted-foreground">{row.original.total_examenes}</span>
        ),
        enableSorting: false,
      },
      {
        id: "tipo_cobro",
        header: t("ordenes.tipoCobro.label"),
        cell: ({ row }) => (
          <TipoCobroBadge
            tipoCobro={row.original.tipo_cobro}
            totalPagado={row.original.total_pagado}
            esCortesia={row.original.es_cortesia}
          />
        ),
        enableSorting: false,
      },
      {
        id: "actions",
        header: () => t("common.actions"),
        cell: ({ row }) => {
          const orden = row.original;
          const transitions = orden.is_active ? getValidTransitions(orden.estado) : [];
          const showDarDeBaja = orden.is_active && canDarDeBajaFn(orden.estado);
          const TRANSITION_ICONS = { Clock, FlaskConical, CheckCircle2 } as const;
          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleViewDetail(orden)}>
                  <Eye className="mr-2 h-4 w-4" />
                  {t("common.view")}
                </DropdownMenuItem>
                {shouldShowEditButton(!!orden.es_editable, hasPermission("ordenes", "update")) && (
                  <DropdownMenuItem onClick={() => handleEdit(orden)}>
                    <Pencil className="mr-2 h-4 w-4" />
                    {t("ordenes.edit.button")}
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={() => handleDownloadRecibo(orden)}>
                  <FileText className="mr-2 h-4 w-4" />
                  {t("ordenes.recibos.download")}
                </DropdownMenuItem>
                {orden.is_active &&
                  canPagar(
                    orden.tipo_cobro,
                    orden.estado,
                    orden.pagada,
                    orden.total,
                    orden.es_cortesia
                  ) && (
                    <DropdownMenuItem onClick={() => handlePagar(orden)}>
                      <CreditCard className="mr-2 h-4 w-4" />
                      {t("ordenes.pago.pay")}
                    </DropdownMenuItem>
                  )}
                {orden.is_active &&
                  canAnularPago(orden.tipo_cobro, orden.estado, orden.es_cortesia) && (
                    <DropdownMenuItem
                      onClick={() => handleAnularPago(orden)}
                      className="text-destructive focus:text-destructive"
                    >
                      <CreditCard className="mr-2 h-4 w-4" />
                      {t("ordenes.pago.cancelPayment")}
                    </DropdownMenuItem>
                  )}
                {transitions.map((target) => {
                  const meta = getTransitionMeta(target);
                  if (!meta) return null;
                  const Icon = TRANSITION_ICONS[meta.icon];
                  return (
                    <DropdownMenuItem key={target} onClick={() => handleTransition(orden, target)}>
                      <Icon className="mr-2 h-4 w-4" />
                      {t(meta.labelKey)}
                    </DropdownMenuItem>
                  );
                })}
                {showDarDeBaja && (
                  <DropdownMenuItem onClick={() => handleCancelOrder(orden)}>
                    <Ban className="mr-2 h-4 w-4" />
                    {t("ordenes.cancelOrder")}
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => handleToggleStatus(orden)}>
                  {orden.is_active ? (
                    <>
                      <XCircle className="mr-2 h-4 w-4" />
                      {t("common.deactivate")}
                    </>
                  ) : (
                    <>
                      <CheckCircle className="mr-2 h-4 w-4" />
                      {t("common.activate")}
                    </>
                  )}
                </DropdownMenuItem>
                {!orden.is_active && (
                  <DropdownMenuItem
                    onClick={() => handleDelete(orden)}
                    className="text-destructive focus:text-destructive"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    {t("common.delete")}
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          );
        },
        enableSorting: false,
      },
    ],
    [
      t,
      handleViewDetail,
      handleCancelOrder,
      handleTransition,
      handleToggleStatus,
      handleDelete,
      handleDownloadRecibo,
      handlePagar,
      handleAnularPago,
      handleEdit,
      hasPermission,
      isAllSucursalesSelected,
    ]
  );

  // Server-side data table
  const {
    table,
    data,
    isLoading,
    error,
    totalCount,
    refetch,
    searchTerm,
    setSearchTerm,
    filters,
    setFilter,
    clearFilters,
    hasActiveFilters,
  } = useServerDataTable({
    columns,
    fetchFn: getOrdenes,
    queryKey: "ordenes",
    storageKey: "ordenes_page_size",
    defaultPageSize: 20,
    filters: [
      { key: "is_active", defaultValue: "all", urlParam: "status" },
      { key: "estado", defaultValue: "all" },
      { key: "prioridad", defaultValue: "all" },
      { key: "tipo_servicio", defaultValue: "all" },
      { key: "sucursal", defaultValue: "all" },
      { key: "fecha_desde", defaultValue: "" },
      { key: "fecha_hasta", defaultValue: "" },
      { key: "pagada", defaultValue: "all" },
    ],
    initialFilters: {
      sucursal: getInitialSucursalFilter(),
    },
  });

  // Sincronizar filtro de sucursal cuando cambia el contexto
  useEffect(() => {
    if (!isAllSucursalesSelected && sucursalActiva && sucursalActiva.id !== ALL_SUCURSALES_ID) {
      const sucursalId = String(sucursalActiva.id);
      if (filters.sucursal !== sucursalId) {
        setFilter("sucursal", sucursalId);
      }
    }
  }, [sucursalActiva, isAllSucursalesSelected, filters.sucursal, setFilter]);

  const handlePagoSuccess = useCallback(
    (result: PagoOrdenResult) => {
      const vuelto = parseFloat(result.vuelto);
      if (vuelto > 0) {
        toast.success(t("ordenes.pago.successPayWithVuelto", { vuelto: vuelto.toFixed(2) }));
      } else {
        toast.success(t("ordenes.pago.successPay"));
      }
      refetch();
    },
    [t, refetch]
  );

  // Total de exámenes en la página actual
  const totalExamenesPage = useMemo(
    () => data.reduce((sum, orden) => sum + (orden.total_examenes ?? 0), 0),
    [data]
  );

  // Aplicar filtro de estado si viene del dashboard con filterStatus
  useEffect(() => {
    const state = location.state as { filterStatus?: string } | null;
    if (state?.filterStatus) {
      setFilter("estado", state.filterStatus);
      window.history.replaceState({}, "");
    }
  }, [location.state, setFilter]);

  const columnLabels: Record<string, string> = {
    numero: t("ordenes.columns.number"),
    fecha: t("ordenes.columns.date"),
    prioridad: t("ordenes.columns.priority"),
    estado: t("ordenes.columns.status"),
    sucursal_nombre: t("ordenes.columns.branch"),
    cliente_nombre: t("ordenes.columns.client"),
  };

  // Mobile card render
  const renderMobileCard = useCallback(
    (orden: OrdenListItem) => {
      const transitions = orden.is_active ? getValidTransitions(orden.estado) : [];
      const showDarDeBaja = orden.is_active && canDarDeBajaFn(orden.estado);
      const TRANSITION_ICONS = { Clock, FlaskConical, CheckCircle2 } as const;
      return (
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <button
              className="font-mono text-sm font-medium text-primary hover:underline"
              onClick={() => handleViewDetail(orden)}
            >
              {orden.numero}
            </button>
            <EstadoOrdenBadge estado={orden.estado} label={orden.estado_display} />
          </div>
          <p className="font-medium">{orden.paciente_nombre}</p>
          {isAllSucursalesSelected && orden.sucursal_nombre && (
            <span className="text-xs text-muted-foreground">{orden.sucursal_nombre}</span>
          )}
          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <span>{new Date(orden.fecha).toLocaleDateString()}</span>
            <Badge variant={getPrioridadBadgeVariant(orden.prioridad)}>
              {orden.prioridad_display}
            </Badge>
          </div>
          <div className="flex items-center justify-between mt-1">
            <span className="text-sm text-muted-foreground">
              {orden.total_examenes} {t("ordenes.columns.exams").toLowerCase()}
            </span>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-7 w-7">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleViewDetail(orden)}>
                  <Eye className="mr-2 h-4 w-4" />
                  {t("common.view")}
                </DropdownMenuItem>
                {shouldShowEditButton(!!orden.es_editable, hasPermission("ordenes", "update")) && (
                  <DropdownMenuItem onClick={() => handleEdit(orden)}>
                    <Pencil className="mr-2 h-4 w-4" />
                    {t("ordenes.edit.button")}
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={() => handleDownloadRecibo(orden)}>
                  <FileText className="mr-2 h-4 w-4" />
                  {t("ordenes.recibos.download")}
                </DropdownMenuItem>
                {orden.is_active &&
                  canPagar(
                    orden.tipo_cobro,
                    orden.estado,
                    orden.pagada,
                    orden.total,
                    orden.es_cortesia
                  ) && (
                    <DropdownMenuItem onClick={() => handlePagar(orden)}>
                      <CreditCard className="mr-2 h-4 w-4" />
                      {t("ordenes.pago.pay")}
                    </DropdownMenuItem>
                  )}
                {orden.is_active &&
                  canAnularPago(orden.tipo_cobro, orden.estado, orden.es_cortesia) && (
                    <DropdownMenuItem
                      onClick={() => handleAnularPago(orden)}
                      className="text-destructive focus:text-destructive"
                    >
                      <CreditCard className="mr-2 h-4 w-4" />
                      {t("ordenes.pago.cancelPayment")}
                    </DropdownMenuItem>
                  )}
                {transitions.map((target) => {
                  const meta = getTransitionMeta(target);
                  if (!meta) return null;
                  const Icon = TRANSITION_ICONS[meta.icon];
                  return (
                    <DropdownMenuItem key={target} onClick={() => handleTransition(orden, target)}>
                      <Icon className="mr-2 h-4 w-4" />
                      {t(meta.labelKey)}
                    </DropdownMenuItem>
                  );
                })}
                {showDarDeBaja && (
                  <DropdownMenuItem onClick={() => handleCancelOrder(orden)}>
                    <Ban className="mr-2 h-4 w-4" />
                    {t("ordenes.cancelOrder")}
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => handleToggleStatus(orden)}>
                  {orden.is_active ? (
                    <>
                      <XCircle className="mr-2 h-4 w-4" />
                      {t("common.deactivate")}
                    </>
                  ) : (
                    <>
                      <CheckCircle className="mr-2 h-4 w-4" />
                      {t("common.activate")}
                    </>
                  )}
                </DropdownMenuItem>
                {!orden.is_active && (
                  <DropdownMenuItem
                    onClick={() => handleDelete(orden)}
                    className="text-destructive focus:text-destructive"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    {t("common.delete")}
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      );
    },
    [
      t,
      handleViewDetail,
      handleCancelOrder,
      handleTransition,
      handleToggleStatus,
      handleDelete,
      handleDownloadRecibo,
      handlePagar,
      handleAnularPago,
      handleEdit,
      hasPermission,
      isAllSucursalesSelected,
    ]
  );

  return (
    <div className="flex flex-col gap-4 sm:gap-6 p-4 sm:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
        <div className="flex items-center gap-3">
          <ClipboardList className="h-6 w-6 sm:h-8 sm:w-8 text-primary" />
          <div>
            <h1 className="text-lg sm:text-2xl font-bold">{t("ordenes.title")}</h1>
            <p className="text-xs sm:text-sm text-muted-foreground hidden sm:block">
              {t("ordenes.description")}
            </p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="w-full sm:w-auto overflow-x-auto">
          <TabsTrigger value="ordenes" className="flex-1 sm:flex-none text-xs sm:text-sm">
            {t("ordenes.tabs.ordenes")}
          </TabsTrigger>
          <TabsTrigger value="examenes" className="flex-1 sm:flex-none text-xs sm:text-sm">
            {t("ordenes.tabs.porExamenes")}
          </TabsTrigger>
          <TabsTrigger value="cotizaciones" className="flex-1 sm:flex-none text-xs sm:text-sm">
            {t("ordenes.tabs.cotizaciones")}
          </TabsTrigger>
        </TabsList>

        {/* Tab: Órdenes */}
        <TabsContent value="ordenes" className="flex flex-col gap-4 mt-4">
          {/* Actions bar */}
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              size={isMobile ? "sm" : "default"}
              onClick={() => refetch()}
              disabled={isLoading}
            >
              <RefreshCw
                className={`h-4 w-4 ${isLoading ? "animate-spin" : ""} ${isMobile ? "" : "mr-2"}`}
              />
              {!isMobile && t("common.refresh")}
            </Button>
            <Button
              size={isMobile ? "sm" : "default"}
              onClick={() => setIsFormOpen(true)}
              disabled={isAllSucursalesSelected}
              title={isAllSucursalesSelected ? t("ordenes.selectSucursalToCreate") : undefined}
            >
              <Plus className={`h-4 w-4 ${isMobile ? "" : "mr-2"}`} />
              {isMobile ? t("common.new") : t("ordenes.newOrder")}
            </Button>
          </div>

          {/* Filters */}
          <Card>
            <CardContent className="pt-4 sm:pt-6">
              <div className="flex flex-wrap items-center gap-2">
                {/* Search */}
                <div className="relative w-full sm:w-auto sm:min-w-[340px] sm:flex-1 sm:max-w-[520px]">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    placeholder={t("ordenes.searchPlaceholder")}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9"
                  />
                </div>

                {/* Sucursal */}
                {isAllSucursalesSelected ? (
                  <Select value={filters.sucursal} onValueChange={(v) => setFilter("sucursal", v)}>
                    <SelectTrigger className="w-[calc(50%-4px)] sm:w-[160px]">
                      <SelectValue placeholder={t("ordenes.columns.branch")} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t("sucursal.allBranches")}</SelectItem>
                      {sucursales.map((s) => (
                        <SelectItem key={s.id} value={String(s.id)}>
                          {s.nombre}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <div className="hidden sm:flex items-center h-9 px-3 border rounded-md bg-muted/50 text-sm">
                    <span className="text-muted-foreground mr-1.5">
                      {t("ordenes.columns.branch")}:
                    </span>
                    <span className="font-medium truncate max-w-[120px]">
                      {sucursalActiva?.nombre || "-"}
                    </span>
                  </div>
                )}

                {/* Estado */}
                <Select value={filters.estado} onValueChange={(v) => setFilter("estado", v)}>
                  <SelectTrigger className="w-[calc(50%-4px)] sm:w-[155px]">
                    <SelectValue placeholder={t("ordenes.filterByStatus")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t("ordenes.allStatus")}</SelectItem>
                    <SelectItem value="EN_EXTRACCION">{t("ordenes.status.extraction")}</SelectItem>
                    <SelectItem value="PENDIENTE">{t("ordenes.status.pending")}</SelectItem>
                    <SelectItem value="CON_RESULTADO">{t("ordenes.status.withResults")}</SelectItem>
                    <SelectItem value="FINALIZADA">{t("ordenes.status.finalized")}</SelectItem>
                    <SelectItem value="BAJA">{t("ordenes.status.cancelled")}</SelectItem>
                  </SelectContent>
                </Select>

                {/* Prioridad */}
                <Select value={filters.prioridad} onValueChange={(v) => setFilter("prioridad", v)}>
                  <SelectTrigger className="w-[calc(50%-4px)] sm:w-[145px]">
                    <SelectValue placeholder={t("ordenes.filterByPriority")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t("ordenes.allPriorities")}</SelectItem>
                    <SelectItem value="NORMAL">{t("ordenes.priority.normal")}</SelectItem>
                    <SelectItem value="URGENTE">{t("ordenes.priority.urgent")}</SelectItem>
                    <SelectItem value="STAT">{t("ordenes.priority.stat")}</SelectItem>
                  </SelectContent>
                </Select>

                {/* Tipo de Servicio */}
                <Select
                  value={filters.tipo_servicio}
                  onValueChange={(v) => setFilter("tipo_servicio", v)}
                >
                  <SelectTrigger className="w-[calc(50%-4px)] sm:w-[155px]">
                    <SelectValue placeholder={t("ordenes.filterByServiceType")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t("ordenes.allServiceTypes")}</SelectItem>
                    <SelectItem value="AMBULATORIO">
                      {t("ordenes.serviceType.ambulatorio")}
                    </SelectItem>
                    <SelectItem value="EMERGENCIA">
                      {t("ordenes.serviceType.emergencia")}
                    </SelectItem>
                    <SelectItem value="HOSPITALIZACION">
                      {t("ordenes.serviceType.hospitalizacion")}
                    </SelectItem>
                    <SelectItem value="DOMICILIO">{t("ordenes.serviceType.domicilio")}</SelectItem>
                  </SelectContent>
                </Select>

                {/* Activo/Inactivo */}
                <Select value={filters.is_active} onValueChange={(v) => setFilter("is_active", v)}>
                  <SelectTrigger className="w-[calc(50%-4px)] sm:w-[130px]">
                    <SelectValue placeholder={t("common.status")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t("ordenes.allStatus")}</SelectItem>
                    <SelectItem value="true">{t("common.active")}</SelectItem>
                    <SelectItem value="false">{t("common.inactive")}</SelectItem>
                  </SelectContent>
                </Select>

                {/* Pago */}
                <Select value={filters.pagada} onValueChange={(v) => setFilter("pagada", v)}>
                  <SelectTrigger className="w-[calc(50%-4px)] sm:w-[155px]">
                    <SelectValue placeholder={t("ordenes.pago.columns.payment")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t("ordenes.pago.filterAll")}</SelectItem>
                    <SelectItem value="true">{t("ordenes.pago.filterPaid")}</SelectItem>
                    <SelectItem value="false">{t("ordenes.pago.filterPending")}</SelectItem>
                  </SelectContent>
                </Select>

                {/* Fecha */}
                <DateRangeFilter
                  desde={filters.fecha_desde}
                  hasta={filters.fecha_hasta}
                  onChange={(desde, hasta) => {
                    setFilter("fecha_desde", desde);
                    setFilter("fecha_hasta", hasta);
                  }}
                  className="w-full sm:w-[200px]"
                />

                {/* Limpiar */}
                {hasActiveFilters && (
                  <Button
                    variant="secondary"
                    size="icon"
                    onClick={clearFilters}
                    className="shrink-0 h-9 w-9"
                    title={t("common.clearFilters")}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Error */}
          {error && (
            <div className="flex items-center gap-2 rounded-md bg-destructive/10 p-4 text-destructive">
              <AlertCircle className="h-5 w-5" />
              <span>{error}</span>
            </div>
          )}

          {/* Table */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 flex-wrap">
                  <CardDescription>
                    {t("ordenes.showingResults", { count: totalCount })}
                  </CardDescription>
                  {totalExamenesPage > 0 && (
                    <CardDescription className="flex items-center gap-1">
                      <TestTubes className="h-3.5 w-3.5" />
                      {t("ordenes.totalExamsInPage", { count: totalExamenesPage })}
                    </CardDescription>
                  )}
                </div>
                {!isMobile && <DataTableViewOptions table={table} columnLabels={columnLabels} />}
              </div>
            </CardHeader>
            <CardContent>
              <ResponsiveTable
                table={table}
                columns={columns}
                isLoading={isLoading}
                emptyMessage={t("ordenes.noOrdenes")}
                hideOnTablet={["total_examenes", "fecha", "cliente_nombre"]}
                renderMobileCard={renderMobileCard}
              />
              <DataTablePagination table={table} totalRows={totalCount} />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Por Exámenes */}
        <TabsContent value="examenes" className="mt-4">
          <OrdenExamenesTab />
        </TabsContent>

        {/* Tab: Cotizaciones */}
        <TabsContent value="cotizaciones" className="mt-4">
          <CotizacionesCatalogo />
        </TabsContent>
      </Tabs>

      {/* Dialogs */}
      <OrdenFormDialog
        open={isFormOpen}
        onOpenChange={(open) => {
          setIsFormOpen(open);
          if (!open) {
            setInitialPacienteId(null);
            setInitialPacienteNombre("");
            setInitialPacienteDesc("");
            setInitialPacienteEdadYears(undefined);
          }
        }}
        onSuccess={handleFormSuccess}
        initialPacienteId={initialPacienteId}
        initialPacienteNombre={initialPacienteNombre}
        initialPacienteDesc={initialPacienteDesc}
        initialPacienteEdadYears={initialPacienteEdadYears}
      />

      {selectedOrdenId && (
        <OrdenDetailSheet
          open={isDetailOpen}
          onOpenChange={setIsDetailOpen}
          ordenId={selectedOrdenId}
        />
      )}

      {editOrdenId && (
        <OrdenEditFormDialog
          open={isEditOpen}
          onOpenChange={(open) => {
            setIsEditOpen(open);
            if (!open) setEditOrdenId(null);
          }}
          ordenId={editOrdenId}
        />
      )}

      <ConfirmDialog
        open={isConfirmOpen}
        onOpenChange={setIsConfirmOpen}
        title={
          confirmAction === "activate"
            ? t("ordenes.confirmActivate")
            : confirmAction === "delete"
              ? t("ordenes.confirmDelete")
              : t("ordenes.confirmDeactivate")
        }
        description={
          confirmAction === "activate"
            ? t("ordenes.confirmActivateDesc", { numero: selectedOrden?.numero })
            : confirmAction === "delete"
              ? t("ordenes.confirmDeleteDesc", { numero: selectedOrden?.numero })
              : t("ordenes.confirmDeactivateDesc", { numero: selectedOrden?.numero })
        }
        onConfirm={handleConfirmAction}
        variant={
          confirmAction === "deactivate" || confirmAction === "delete" ? "destructive" : "default"
        }
      />

      {/* Cancel order dialog */}
      <ConfirmDialog
        open={isCancelOpen}
        onOpenChange={setIsCancelOpen}
        title={t("ordenes.confirmCancel")}
        description={
          <div className="space-y-3">
            <p>{t("ordenes.confirmCancelDesc", { numero: selectedOrden?.numero })}</p>
            <Input
              placeholder={t("ordenes.cancelReasonPlaceholder")}
              value={cancelMotivo}
              onChange={(e) => setCancelMotivo(e.target.value)}
            />
          </div>
        }
        onConfirm={handleConfirmCancel}
        variant="destructive"
      />

      {/* Transition confirm dialog */}
      <ConfirmDialog
        open={!!transitionTarget}
        onOpenChange={(open) => !open && setTransitionTarget(null)}
        title={t("ordenes.transitions.confirmTitle")}
        description={
          transitionTarget
            ? t("ordenes.transitions.confirmDesc", {
                numero: transitionTarget.orden.numero,
                from: transitionTarget.orden.estado_display,
                to: getTransitionMeta(transitionTarget.estado)
                  ? t(getTransitionMeta(transitionTarget.estado)!.labelKey)
                  : transitionTarget.estado,
              })
            : ""
        }
        confirmText={
          transitionTarget && getTransitionMeta(transitionTarget.estado)
            ? t(getTransitionMeta(transitionTarget.estado)!.labelKey)
            : t("common.confirm")
        }
        onConfirm={handleConfirmTransition}
        isLoading={cambiarEstadoMutation.isPending}
      />

      {/* Pago directo */}
      <PagoOrdenFormDialog
        open={isPagoOpen}
        onOpenChange={(open) => {
          setIsPagoOpen(open);
          if (!open) setPagoOrden(null);
        }}
        orden={pagoOrden}
        onPagoSuccess={handlePagoSuccess}
      />

      {/* Anular pago */}
      <ConfirmDialog
        open={isAnularPagoOpen}
        onOpenChange={(open) => {
          setIsAnularPagoOpen(open);
          if (!open) setPagoOrden(null);
        }}
        title={t("ordenes.pago.cancelPayment")}
        description={t("ordenes.pago.cancelPaymentConfirm", { numero: pagoOrden?.numero })}
        onConfirm={() => pagoOrden && anularPagoMutation.mutate(pagoOrden.id)}
        variant="destructive"
        isLoading={anularPagoMutation.isPending}
      />
    </div>
  );
}
